import { z } from 'zod';
import { CognitoSubSchema, TimestampSchema } from '../common.js';
export const AgentVoiceProfileIdSchema = z.literal('primary');
export const OpenAICustomVoiceSchema = z.object({ id: z.string().min(1) }).strict();
export const OpenAIVoiceSchema = z.union([z.string().min(1), OpenAICustomVoiceSchema]);
export const VoiceConsentLanguageSchema = z.enum([
    'de',
    'en',
    'es',
    'fr',
    'hi',
    'id',
    'it',
    'ja',
    'ko',
    'nl',
    'pl',
    'pt',
    'ru',
    'uk',
    'vi',
    'zh',
]);
/** Exact scripts required by OpenAI's Custom Voices consent API. */
export const OPENAI_VOICE_CONSENT_PHRASES = {
    de: 'Ich bin der Eigentümer dieser Stimme und bin damit einverstanden, dass OpenAI diese Stimme zur Erstellung eines synthetischen Stimmmodells verwendet.',
    en: 'I am the owner of this voice and I consent to OpenAI using this voice to create a synthetic voice model.',
    es: 'Soy el propietario de esta voz y doy mi consentimiento para que OpenAI la utilice para crear un modelo de voz sintética.',
    fr: "Je suis le propriétaire de cette voix et j'autorise OpenAI à utiliser cette voix pour créer un modèle de voix synthétique.",
    hi: 'मैं इस आवाज का मालिक हूं और मैं सिंथेटिक आवाज मॉडल बनाने के लिए OpenAI को इस आवाज का उपयोग करने की सहमति देता हूं',
    id: 'Saya adalah pemilik suara ini dan saya memberikan persetujuan kepada OpenAI untuk menggunakan suara ini guna membuat model suara sintetis.',
    it: 'Sono il proprietario di questa voce e acconsento che OpenAI la utilizzi per creare un modello di voce sintetica.',
    ja: '私はこの音声の所有者であり、OpenAIがこの音声を使用して音声合成 モデルを作成することを承認します。',
    ko: '나는 이 음성의 소유자이며 OpenAI가 이 음성을 사용하여 음성 합성 모델을 생성할 것을 허용합니다.',
    nl: 'Ik ben de eigenaar van deze stem en ik geef OpenAI toestemming om deze stem te gebruiken om een synthetisch stemmodel te maken.',
    pl: 'Jestem właścicielem tego głosu i wyrażam zgodę na wykorzystanie go przez OpenAI w celu utworzenia syntetycznego modelu głosu.',
    pt: 'Eu sou o proprietário desta voz e autorizo o OpenAI a usá-la para criar um modelo de voz sintética.',
    ru: 'Я являюсь владельцем этого голоса и даю согласие OpenAI на использование этого голоса для создания модели синтетического голоса.',
    uk: 'Я є власником цього голосу і даю згоду OpenAI використовувати цей голос для створення синтетичної голосової моделі.',
    vi: 'Tôi là chủ sở hữu giọng nói này và tôi đồng ý cho OpenAI sử dụng giọng nói này để tạo mô hình giọng nói tổng hợp.',
    zh: '我是此声音的拥有者并授权OpenAI使用此声音创建语音合成模型',
};
export const VoiceCloneStatusSchema = z.enum([
    'awaiting-consent',
    'processing',
    'ready',
    'active',
    'failed',
]);
export const VoiceProfileActorSchema = z.union([
    CognitoSubSchema,
    z.literal('service:openai-voice-clone-worker'),
]);
export const VoiceConsentMimeSchema = z.enum([
    'audio/mpeg',
    'audio/mp4',
    'audio/ogg',
    'audio/aac',
    'audio/flac',
    'audio/wav',
    'audio/x-wav',
    'audio/webm',
    'video/mp4',
    'video/webm',
]);
export const VoiceMobileRecordingMimeSchema = z.enum([
    'audio/mpeg',
    'audio/mp4',
    'audio/ogg',
    'audio/aac',
    'audio/flac',
    'audio/wav',
    'audio/x-wav',
    'audio/webm',
]);
export const VoiceSampleSourceSchema = z.enum(['agent-video', 'mobile-recording']);
export const AgentVoiceProfileFieldsSchema = z
    .object({
    tenantId: z.string().uuid(),
    agentId: z.string().uuid(),
    profileId: AgentVoiceProfileIdSchema,
    setupId: z.string().uuid(),
    provider: z.literal('openai'),
    status: VoiceCloneStatusSchema,
    name: z.string().min(1).max(64),
    sampleSource: VoiceSampleSourceSchema,
    sourceMediaId: z.string().uuid().optional(),
    sourceMime: z
        .string()
        .regex(/^video\//)
        .optional(),
    sourceBytes: z
        .number()
        .int()
        .positive()
        .max(100 * 1024 * 1024)
        .optional(),
    captureId: z.string().uuid().optional(),
    sampleS3Key: z.string().min(1).max(1_024).optional(),
    sampleMime: VoiceMobileRecordingMimeSchema.optional(),
    sampleBytes: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024)
        .optional(),
    consentLanguage: VoiceConsentLanguageSchema,
    triggerS3Key: z.string().min(1).max(1_024),
    consentS3Key: z.string().min(1).max(1_024),
    consentMime: VoiceConsentMimeSchema,
    consentBytes: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024),
    onboardingCompletedAt: TimestampSchema.optional(),
    onboardingCompletedBy: VoiceProfileActorSchema.optional(),
    providerConsentId: z.string().min(1).optional(),
    providerVoiceId: z.string().min(1).optional(),
    failureCode: z.string().min(1).max(80).optional(),
    failureMessage: z.string().min(1).max(500).optional(),
    createdAt: TimestampSchema,
    createdBy: CognitoSubSchema,
    updatedAt: TimestampSchema,
    updatedBy: VoiceProfileActorSchema,
    activatedAt: TimestampSchema.optional(),
    activatedBy: CognitoSubSchema.optional(),
})
    .strict();
export const AgentVoiceProfileSchema = AgentVoiceProfileFieldsSchema.superRefine((profile, ctx) => {
    const hasVideoSource = profile.sourceMediaId !== undefined &&
        profile.sourceMime !== undefined &&
        profile.sourceBytes !== undefined;
    const hasAnyVideoSource = profile.sourceMediaId !== undefined ||
        profile.sourceMime !== undefined ||
        profile.sourceBytes !== undefined;
    const hasMobileSource = profile.captureId !== undefined &&
        profile.sampleS3Key !== undefined &&
        profile.sampleMime !== undefined &&
        profile.sampleBytes !== undefined;
    const hasAnyMobileSource = profile.captureId !== undefined ||
        profile.sampleS3Key !== undefined ||
        profile.sampleMime !== undefined ||
        profile.sampleBytes !== undefined;
    if (profile.sampleSource === 'agent-video' && (!hasVideoSource || hasAnyMobileSource)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sampleSource'],
            message: 'Agent-video profiles require only the agent media source fields.',
        });
    }
    if (profile.sampleSource === 'mobile-recording' && (!hasMobileSource || hasAnyVideoSource)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sampleSource'],
            message: 'Mobile profiles require only the capture sample fields.',
        });
    }
    const hasOnboardingCompletion = profile.onboardingCompletedAt !== undefined && profile.onboardingCompletedBy !== undefined;
    const hasAnyOnboardingCompletion = profile.onboardingCompletedAt !== undefined || profile.onboardingCompletedBy !== undefined;
    if (hasAnyOnboardingCompletion !== hasOnboardingCompletion) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['onboardingCompletedAt'],
            message: 'Onboarding completion requires both its timestamp and actor.',
        });
    }
    const requiresCompletedOnboarding = profile.status === 'processing' ||
        profile.status === 'ready' ||
        profile.status === 'active' ||
        profile.providerConsentId !== undefined ||
        profile.providerVoiceId !== undefined;
    if (requiresCompletedOnboarding && !hasOnboardingCompletion) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['onboardingCompletedAt'],
            message: 'Provider processing cannot begin before onboarding is complete.',
        });
    }
    const hasVoice = profile.providerVoiceId !== undefined;
    if ((profile.status === 'ready' || profile.status === 'active') !== hasVoice) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerVoiceId'],
            message: 'Ready and active profiles must have a provider voice id.',
        });
    }
    const hasActivation = profile.activatedAt !== undefined && profile.activatedBy !== undefined;
    if ((profile.status === 'active') !== hasActivation) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activatedAt'],
            message: 'Only an active profile carries activation metadata.',
        });
    }
    const hasFailure = profile.failureCode !== undefined && profile.failureMessage !== undefined;
    if ((profile.status === 'failed') !== hasFailure) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['failureCode'],
            message: 'Only a failed profile carries failure metadata.',
        });
    }
});
//# sourceMappingURL=agentVoiceProfile.js.map